import {
  afterNextRender,
  Component,
  effect,
  inject,
  OnInit,
} from '@angular/core';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { firstValueFrom, map, Observable, takeUntil } from 'rxjs';
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { InputOption } from '../../../core/models/common/input-option';
import { InputOptionsHelper } from '../../../core/helpers/input-options.helper';
import * as fromStore from '../../../core/store/reducers';
import {
  selectPublicVendor,
  selectVendorLoadingError,
} from '../../../core/store/reducers';
import { Store } from '@ngrx/store';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { isFuture, isToday, parseISO, startOfToday } from 'date-fns';
import { DatetimeHelper } from '../../../core/helpers/datetime.helper';
import { Room, RoomAvailability } from '../../../core/models/entities/room';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import {
  EventType,
  EventTypeId,
} from '../../../core/models/entities/event-type';
import { RoomAmenity } from '../../../core/models/entities/room-amenity';
import { AmenityId } from '../../../core/models/entities/amenity';
import { ToastService } from '../../../core/services/toast.service';
import { Title } from '@angular/platform-browser';
import { availableRoomsActions } from 'angular/app/core/store/available-rooms';
import { cloneDeep } from 'lodash';
import { RoomsPeopleFilter } from '../../../core/store/bookings/bookings.state';
import { BaseOptions } from 'flatpickr/dist/types/options';
import { RoomsService } from '../../../core/services/rooms.service';

@Component({
  selector: 'app-vendor-page',
  animations: [fadeAnimation],
  template: `
    <div class="container py-6 lg:py-12">
      @if (vendor()) {
        <div>
          <!-- ------------------------------------------------------------------------->
          <!-- Header hero -->
          <!-- ------------------------------------------------------------------------->
          <div class="grid grid-cols-1 items-center lg:grid-cols-2 lg:gap-20">
            <div>
              @if (vendor().logo_url) {
                <img
                  class="mb-10 max-h-[68px]"
                  [src]="vendor().logo_url"
                  alt="Logo {{ vendor().name }}"
                  title="Logo {{ vendor().name }}"
                  />
              }
    
              <h1
                >Louer une salle<br /><span class="font-normal italic">{{
                vendor().name
              }}</span></h1
              >
            </div>
    
            <div class="col-span-1 hidden xl:flex xl:justify-center">
              <img
                class="max-h-[450px]"
                ngSrc="vendor-hero.png"
                width="460"
                height="450"
                sizes="25vw"
                alt="Location de salles : {{ vendor().name }}"
                title="Location de salles : {{ vendor().name }}"
                />
            </div>
          </div>
    
          <!-- ------------------------------------------------------------------------->
          <!-- Formulaire de recherche -->
          <!-- ------------------------------------------------------------------------->
          <div
            class="border-secondary-500 mt-10 rounded-lg border p-6 lg:p-8 lg:pb-4 xl:mt-20"
            >
            @if (form) {
              <form novalidate (ngSubmit)="onSubmit()">
                <div class="grid grid-cols-2 items-center gap-x-6">
                  <div class="col-span-2 md:col-span-1">
                    <app-date-input
                      label="Date de votre événement"
                      name="start"
                      [control]="form.get('start')"
                      [flatpickrOptions]="startOptions"
                      placeholder="Sélectionnez votre date"
                    ></app-date-input>
                  </div>
                  <div class="col-span-2 md:col-span-1">
                    <div class="md:translate-y-8 lg:translate-y-4">
                      <app-single-checkbox-input
                        label="<span class='font-medium'>Je suis flexible au niveau des dates</span> <span class='font-extralight'>(+ ou - 7 jours)</span>"
                        name="flexible"
                        [control]="form.get('flexible')"
                      ></app-single-checkbox-input>
                    </div>
                  </div>
                </div>
                <div class="items-end lg:flex lg:space-x-6">
                  <div class="lg:basis-1/3 xl:basis-1/2">
                    <app-select-input
                      label="Type d'événement"
                      name="event_type_id"
                      [options]="eventTypesOptions$ | async"
                      [hidePleaseSelect]="true"
                      [control]="form.get('event_type_id')"
                    ></app-select-input>
                  </div>
                  <div class="lg:grow">
                    <app-select-input
                      label="Taille de salle"
                      name="seats"
                      [hidePleaseSelect]="true"
                      [options]="seatsOptions"
                      [control]="form.get('seats')"
                    ></app-select-input>
                  </div>
                  <div class="lg:-translate-y-6">
                    <app-button type="submit" [fullWidthOnMobile]="true"
                      >Rechercher
                    </app-button>
                  </div>
                </div>
              </form>
            }
          </div>
    
          <!-- ------------------------------------------------------------------------->
          <!-- Liste des salles -->
          <!-- ------------------------------------------------------------------------->
          <div class="mt-10 md:mt-16">
            @if (fetching$ | async) {
              <div
                class="flex justify-center pt-20 pb-[400px]"
                >
                <app-loader></app-loader>
              </div>
            }
            @if ((fetching$ | async) === false) {
              <div [@fadeAnimation]="'in'">
                @if ((rooms$ | async)?.length === 0) {
                  <div class="pt-20">
                    <app-empty-state
                      icon="calendar-times"
                      message="Aucune salle n'a été trouvée"
                      [inline]="true"
                      [showAddButton]="false"
                    ></app-empty-state>
                  </div>
                }
                @if ((rooms$ | async)?.length > 0) {
                  <div
                    class="grid gap-10 md:grid-cols-2 xl:grid-cols-3"
                    >
                    @for (room of rooms$ | async; track room) {
                      <div
                        class="shadow-sidebar col-span-1 flex flex-col rounded-xl"
                        [routerLink]="['./salle', room.id]"
                        >
                        <div class="flex">
                          <div class="cursor-pointer">
                            @if (room.photos[0] || !!room.default_photo) {
                              <img
                        [attr.src]="
                          room.photos[0]?.public_path ?? room.default_photo
                        "
                                alt="{{ room.name }}"
                                title="{{ room.name }}"
                                class="rounded-tl-xl rounded-br-xl"
                                />
                            }
                          </div>
                          <div
                            class="flex w-14 flex-none flex-col items-center justify-between py-2"
                            >
                            @for (icon of roomSelectedIcons.get(room.id); track icon) {
                              <div
                                class="flex basis-1/4 items-center"
                                >
                                @if (icon) {
                                  <i class="fal fa-lg fa-{{ icon }}"></i>
                                }
                              </div>
                            }
                          </div>
                        </div>
                        <div class="flex grow flex-col justify-between gap-8 p-8">
                          <div class="space-y-4">
                            <div>
                              <h3 class="text-lg! font-bold">{{ room.name }}</h3>
                              @if (room.building) {
                                <div class="italic">{{
                                  room.building.name
                                }}</div>
                              }
                            </div>
                            <div class="ml-2 space-y-3">
                              <div class="flex items-center">
                                <div class="w-8 flex-none">
                                  <i class="fal fa-users"></i>
                                </div>
                                Jusqu'à {{ room.max_occupancy }} personnes
                              </div>
                              <div class="flex">
                                <div class="w-8 flex-none">
                                  <i class="fal fa-info-circle"></i>
                                </div>
                                <div class="relative -top-px">
                                  @for (
                                    eventType of room.event_types; track
                                    eventType; let last = $last) {
                                    <span
                                      >
                                      {{ eventType.name }}@if (!last) {
                                      <span>,</span>
                                    }
                                  </span>
                                }
                              </div>
                            </div>
                          </div>
                          @if (room.availability) {
                            <div class="pt-3">
                              @if (room.availability === RoomAvailability.Free) {
                                <app-badge
                                  variant="success"
                                  >Disponible à la date souhaitée
                                </app-badge>
                              }
                              @if (
                                room.availability === RoomAvailability.Partial
                                ) {
                                <app-badge
                                  variant="warning"
                                  >Dates alternatives disponibles
                                </app-badge>
                              }
                              @if (
                                room.availability === RoomAvailability.Occupied
                                ) {
                                <app-badge
                                  variant="danger"
                                  >Non disponible
                                </app-badge>
                              }
                            </div>
                          }
                        </div>
                        <app-button
                          [loading]="fetching$ | async"
                          [attr.data-cy]="'room-details-button'"
                          [routerLink]="['./salle/', room.id]"
                          >Découvrir
                        </app-button>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    } @else if (vendorLoadingError()) {
      <div class="mx-auto w-80 md:w-96">
        <dotlottie-player
          autoplay
          loop
          mode="normal"
          src="/assets/img/404.lottie"
          >
        </dotlottie-player>
      </div>
      <div class="text-center">
        <div class="-mt-8 text-6xl font-bold uppercase">OUPS !</div>
        <div class="mt-4 text-lg font-bold uppercase">
          Nous n'avons pas trouvé la page que vous cherchez.
        </div>
        <p class="mt-4 text-base">
          Si vous avez suivi un lien, il est possible qu'il soit incorrect ou
          expiré.
        </p>
    
        <div class="mt-10">
          <app-button routerLink="/louer-salle" size="sm" icon="arrow-right"
            >Rechercher une salle</app-button
            >
          </div>
        </div>
      } @else {
        <app-loader [centered]="true"></app-loader>
      }
    </div>
    `,
  standalone: false,
})
export class VendorPageComponent
  extends DestroyableComponent
  implements OnInit
{
  RoomAvailability = RoomAvailability;
  vendor = this.store.selectSignal(selectPublicVendor);
  vendorLoadingError = this.store.selectSignal(selectVendorLoadingError);
  form: UntypedFormGroup;
  roomsService = inject(RoomsService);
  eventTypesOptions$: Observable<InputOption[]> =
    InputOptionsHelper.selectOptions$(
      this.store.select(fromStore.selectAllEventTypes),
    ).pipe(
      map((options: InputOption[]) => {
        options.unshift({ label: 'Tous', value: '' });
        return options;
      }),
    );
  fetching$: Observable<boolean> = this.store.select(
    fromStore.selectAvailableRoomsLoading,
  );
  rooms$: Observable<Room[]> = this.store
    .select(fromStore.selectAllAvailableRooms)
    .pipe(
      map((rooms: Room[]) =>
        rooms.filter((room: Room) => {
          return this.roomsService.canUserAccessRoom(room);
        }),
      ),
    );
  roomSelectedIcons: Map<number, string[]> = new Map<number, string[]>();
  seatsOptions: InputOption[] = [
    { label: 'Pas de préférence', value: 1 },
  ].concat(
    // @ts-ignore
    Object.values(RoomsPeopleFilter).map((value) => ({
      label: value.toString(),
      value:
        value === RoomsPeopleFilter.LESS_THAN_20
          ? 19
          : value === RoomsPeopleFilter.BETWEEN_20_AND_100
            ? 100
            : value === RoomsPeopleFilter.MORE_THAN_100
              ? 101
              : 1,
    })),
  );
  startOptions: Partial<BaseOptions>;

  constructor(
    private store: Store,
    private formValidationService: FormValidationService,
    private toastService: ToastService,
    private title: Title,
  ) {
    super();

    afterNextRender(() => {
      import('@dotlottie/player-component');
    });

    effect(async () => {
      if (this.vendor()) {
        this.title.setTitle(
          'Location de salles - ' +
            this.vendor().name +
            ' - Rechercher une salle pour votre événement',
        );

        const filters = await firstValueFrom(
          this.store
            .select(fromStore.selectAvailableRoomsState)
            .pipe(map((state) => state.filters)),
        );
        this.form = new FormGroup({
          vendor_id: new FormControl(this.vendor().id),
          start: new FormControl(
            filters?.start &&
            (isToday(parseISO(filters.start)) ||
              isFuture(parseISO(filters.start)))
              ? [parseISO(filters.start)]
              : [],
          ),
          end: new FormControl(),
          flexible: new FormControl(filters?.flexible ?? true),
          event_type_id: new FormControl(filters?.event_type_id ?? ''),
          seats: new FormControl(filters?.seats ?? 1),
        });

        this.startOptions = {
          minDate: startOfToday(),
        };
        this.findAvailableRooms();
        this.form.valueChanges
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(() => {
            this.findAvailableRooms();
          });
      }
    });
  }

  ngOnInit(): void {
    this.store
      .select(fromStore.selectAvailableRoomsState)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((state) => {
        if (!!state.error) {
          this.toastService.error(state.error);
        }
      });

    this.rooms$.pipe(takeUntil(this.onDestroy$)).subscribe((rooms) => {
      this.roomSelectedIcons.clear();
      rooms.forEach((room: Room) => {
        this.roomSelectedIcons.set(
          room.id,
          this.getFavoriteAmenitiesFromEventType(cloneDeep(room)),
        );
      });
    });
  }

  onSubmit() {
    if (this.formValidationService.validate(this.form)) {
      this.findAvailableRooms();
    }
  }

  private getFavoriteAmenitiesFromEventType(room: Room): string[] {
    let selectedRoomAmenities: RoomAmenity[] = [];
    if (room.event_types.length > 1) {
      selectedRoomAmenities = room.room_amenities;
    } else if (room.event_types.length === 1) {
      const eventType: EventType = room.event_types[0];
      let favoriteAmenitiesIds: number[] = [];
      switch (eventType.id) {
        case EventTypeId.CONFERENCE:
          favoriteAmenitiesIds = [
            AmenityId.AUDIOVISUAL,
            AmenityId.PARKING,
            AmenityId.WIFI,
            AmenityId.FURNITURE,
          ];
          break;
        case EventTypeId.PRIVATE_EVENT:
          favoriteAmenitiesIds = [
            AmenityId.KITCHEN,
            AmenityId.SILVERWARE,
            AmenityId.PARKING,
            AmenityId.REDUCED_MOBILITY,
          ];
          break;
        case EventTypeId.SPORT:
          favoriteAmenitiesIds = [
            AmenityId.RESTROOMS,
            AmenityId.FURNITURE,
            AmenityId.PARKING,
          ];
          break;
        case EventTypeId.CULTURE:
          favoriteAmenitiesIds = [
            AmenityId.KITCHEN,
            AmenityId.STAGE,
            AmenityId.AUDIOVISUAL,
            AmenityId.PARKING,
          ];
          break;
      }
      selectedRoomAmenities = room.room_amenities.filter(
        (roomAmenity: RoomAmenity) => {
          return (
            favoriteAmenitiesIds.includes(roomAmenity.amenity.id) &&
            roomAmenity.amenity_id !== AmenityId.OTHER
          );
        },
      );
      if (selectedRoomAmenities.length < 4) {
        const otherRoomAmenities: RoomAmenity[] = room.room_amenities.filter(
          (roomAmenity: RoomAmenity) => {
            return !favoriteAmenitiesIds.includes(roomAmenity.amenity.id);
          },
        );
        otherRoomAmenities.forEach((roomAmenity: RoomAmenity) => {
          if (selectedRoomAmenities.length < 4) {
            selectedRoomAmenities.push(roomAmenity);
          }
        });
      }
    }

    const shuffledRoomAmenities: RoomAmenity[] = selectedRoomAmenities.sort(
      () => 0.5 - Math.random(),
    );
    selectedRoomAmenities = shuffledRoomAmenities.slice(0, 4);
    const icons = selectedRoomAmenities.map((roomAmenity: RoomAmenity) => {
      return roomAmenity.amenity.icon;
    });
    while (icons.length < 4) {
      icons.push(null);
    }

    return icons;
  }

  private async findAvailableRooms(): Promise<void> {
    let start = null;
    if (this.form.value.start[0]) {
      start = DatetimeHelper.dateToApiFormat(this.form.value.start[0]);
    }
    let end: string;
    if (this.form.value.end?.[0]) {
      end = DatetimeHelper.dateToApiFormat(this.form.value.end[0]);
    } else if (this.form.value.start[0]) {
      end = DatetimeHelper.dateToApiFormat(this.form.value.start[0]);
    }
    this.store.dispatch(
      availableRoomsActions.setFilters({
        filters: {
          ...this.form.value,
          start,
          end,
        },
      }),
    );
  }
}
